import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAdminReferenceOptions, useCreateAdminReferenceOption, useDeleteAdminReferenceOption, useUpdateAdminReferenceOption, } from '@/api/opengater/useLocations';
import { Input } from '@/components/primitives/Input';
import { Toggle } from '@/components/primitives/Toggle';
import { PanelForm, SaveBar, SettingsDanger, SettingsGroup, SettingsLoading, SettingsNote, SettingsRow, } from '@/components/settings/Settings';
import { buildChangedPatch, locationAdminError, locationConfirmationError, locationFieldErrors, requiredTextError, } from './locationRules';
import { createdEntityId } from './creationResult';
import { ReferenceOptionTranslationEditor } from './TranslationEditor';
const empty = { name: '', description: '', has_squads: true };
const draftOf = (option) => ({
    name: option.name,
    description: option.description ?? '',
    has_squads: option.has_squads,
});
const normalized = (draft) => ({
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    has_squads: draft.has_squads,
});
export function ReferenceOptionForm({ optionId, creating = false, onDirtyChange, onCreated, onDeleted, }) {
    const options = useAdminReferenceOptions();
    const option = options.data?.find((row) => row.id === optionId) ?? null;
    const create = useCreateAdminReferenceOption();
    const update = useUpdateAdminReferenceOption();
    const remove = useDeleteAdminReferenceOption();
    const [base, setBase] = useState(creating ? empty : null);
    const [draft, setDraft] = useState(creating ? empty : null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [saveError, setSaveError] = useState(null);
    const [translationDirty, setTranslationDirty] = useState(false);
    const dirtyRef = useRef(false);
    useEffect(() => {
        if (creating || !option || dirtyRef.current)
            return;
        const next = draftOf(option);
        setBase(next);
        setDraft(next);
    }, [creating, option]);
    useEffect(() => {
        if (!creating && options.data && !option && !dirtyRef.current)
            onDeleted();
    }, [creating, onDeleted, option, options.data]);
    const patch = useMemo(() => {
        if (!base || !draft)
            return {};
        return buildChangedPatch(normalized(base), normalized(draft), ['description']);
    }, [base, draft]);
    const dirty = creating
        ? Boolean(draft && (draft.name || draft.description || !draft.has_squads))
        : Object.keys(patch).length > 0;
    useEffect(() => {
        dirtyRef.current = dirty || translationDirty;
    }, [dirty, translationDirty]);
    const nameError = draft ? (requiredTextError(draft.name) ?? fieldErrors.name) : null;
    useEffect(() => onDirtyChange(dirty || translationDirty), [dirty, onDirtyChange, translationDirty]);
    useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
    if (!creating && options.isLoading)
        return _jsx(SettingsLoading, {});
    if ((!creating && ((options.error && !options.data) || !option)) || !base || !draft) {
        return (_jsx(SettingsNote, { tone: "error", children: locationAdminError(options.error, 'Тип опции не найден.') }));
    }
    const submit = async (event) => {
        event.preventDefault();
        if ((!creating && !dirty) || nameError || create.isPending || update.isPending)
            return;
        try {
            if (creating) {
                const beforeIds = (options.data ?? []).map((row) => row.id);
                const response = await create.mutateAsync({
                    name: draft.name.trim(),
                    description: draft.description.trim() || null,
                    has_squads: draft.has_squads,
                });
                let id = createdEntityId(response, beforeIds, []);
                try {
                    const refreshed = await options.refetch({ throwOnError: true });
                    id = createdEntityId(response, beforeIds, (refreshed.data ?? []).map((row) => row.id));
                }
                catch {
                    // The POST succeeded. Catalog refresh can recover independently.
                }
                onCreated(id);
            }
            else if (option) {
                await update.mutateAsync({ id: option.id, patch });
                const refreshed = await options.refetch({ throwOnError: true });
                const authoritative = refreshed.data?.find((row) => row.id === option.id);
                if (!authoritative)
                    throw new Error('Saved reference option was not returned');
                const next = draftOf(authoritative);
                setBase(next);
                setDraft(next);
            }
            setFieldErrors({});
            setSaveError(null);
        }
        catch (error) {
            const fields = locationFieldErrors(error, ['name', 'description', 'has_squads']);
            setFieldErrors(fields);
            if (Object.keys(fields).length === 0) {
                setSaveError(locationConfirmationError(error));
            }
            else {
                setSaveError(null);
            }
        }
    };
    return (_jsxs(PanelForm, { meta: creating ? 'Новый тип опции' : `Тип опции #${option.id}`, onSubmit: submit, children: [_jsxs(SettingsGroup, { title: creating ? 'Новый тип опции' : 'Тип опции', children: [_jsx(SettingsRow, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", wide: true, error: nameError, children: _jsx(Input, { "aria-label": "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0442\u0438\u043F\u0430 \u043E\u043F\u0446\u0438\u0438", "aria-invalid": Boolean(nameError), value: draft.name, disabled: create.isPending || update.isPending, onChange: (event) => {
                                setDraft({ ...draft, name: event.target.value });
                                setFieldErrors((current) => ({ ...current, name: undefined }));
                                setSaveError(null);
                                create.reset();
                                update.reset();
                            } }) }), _jsx(SettingsRow, { label: "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435", wide: true, error: fieldErrors.description, children: _jsx(Input, { "aria-label": "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435 \u0442\u0438\u043F\u0430 \u043E\u043F\u0446\u0438\u0438", value: draft.description, disabled: create.isPending || update.isPending, onChange: (event) => {
                                setDraft({ ...draft, description: event.target.value });
                                setFieldErrors((current) => ({ ...current, description: undefined }));
                                setSaveError(null);
                                create.reset();
                                update.reset();
                            } }) }), _jsx(SettingsRow, { label: "\u0418\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u0435\u0442 \u0441\u043A\u0432\u0430\u0434\u044B", error: fieldErrors.has_squads, children: _jsx(Toggle, { "aria-label": "\u0422\u0438\u043F \u043E\u043F\u0446\u0438\u0438 \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u0435\u0442 \u0441\u043A\u0432\u0430\u0434\u044B", on: draft.has_squads, disabled: create.isPending || update.isPending, onChange: (value) => {
                                setDraft({ ...draft, has_squads: value });
                                setFieldErrors((current) => ({ ...current, has_squads: undefined }));
                                setSaveError(null);
                                create.reset();
                                update.reset();
                            } }) })] }), !creating && base.has_squads && !draft.has_squads && (_jsx(SettingsNote, { tone: "warn", children: "\u0421\u0443\u0449\u0435\u0441\u0442\u0432\u0443\u044E\u0449\u0438\u0435 \u0441\u0432\u044F\u0437\u0438 \u0441\u043E \u0441\u043A\u0432\u0430\u0434\u0430\u043C\u0438 \u0441\u043E\u0445\u0440\u0430\u043D\u044F\u0442\u0441\u044F. \u041D\u043E\u0432\u044B\u0435 \u0441\u043A\u0432\u0430\u0434\u044B \u043F\u0440\u0438\u0432\u044F\u0437\u0430\u0442\u044C \u0431\u0443\u0434\u0435\u0442 \u043D\u0435\u043B\u044C\u0437\u044F." })), !creating && (_jsx(ReferenceOptionTranslationEditor, { id: option.id, onDirtyChange: setTranslationDirty })), !creating && (_jsx(SettingsDanger, { label: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0442\u0438\u043F \u043E\u043F\u0446\u0438\u0438", confirmText: "\u041E\u043F\u0446\u0438\u044F \u0431\u0443\u0434\u0435\u0442 \u0443\u0434\u0430\u043B\u0435\u043D\u0430 \u0438\u0437 \u0432\u0441\u0435\u0445 \u043B\u043E\u043A\u0430\u0446\u0438\u0439. \u041F\u0440\u043E\u0434\u043E\u043B\u0436\u0438\u0442\u044C?", pending: remove.isPending, disabled: dirty || translationDirty, error: remove.error ? locationAdminError(remove.error) : undefined, onConfirm: () => remove.mutate(option.id, { onSuccess: onDeleted }) })), _jsx(SaveBar, { dirty: creating || dirty, saving: create.isPending || update.isPending, saved: update.isSuccess, canSave: (creating || dirty) && !nameError && !create.isPending && !update.isPending, error: saveError ??
                    (create.error || update.error
                        ? locationAdminError(create.error ?? update.error)
                        : undefined), onReset: creating
                    ? onDeleted
                    : () => {
                        setDraft(base);
                        setFieldErrors({});
                        setSaveError(null);
                        update.reset();
                    }, text: creating ? 'Новый тип опции' : undefined, submitLabel: creating ? 'Создать' : undefined })] }));
}
